import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Resolve backend-served asset paths (like /api/uploads/xxx.png) to full URL
export const assetUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

export const getConfig = () => api.get("/config").then((r) => r.data);
export const getPricing = () => api.get("/pricing").then((r) => r.data);
export const createOrder = (payload) => api.post("/orders", payload).then((r) => r.data);
export const trackOrder = (q) => api.get(`/orders/track`, { params: { q } }).then((r) => r.data);

export const getPlans = () => api.get("/plans").then((r) => r.data);
export const createSubscription = (payload) => api.post("/subscriptions", payload).then((r) => r.data);
export const getReferral = (phone) =>
  api.get(`/referrals/${encodeURIComponent(phone)}`).then((r) => r.data);

// Auth-gated (Bearer token attached by interceptor)
export const meOrders = () => api.get("/me/orders").then((r) => r.data);
export const meSubscriptions = () => api.get("/me/subscriptions").then((r) => r.data);
export const meAddresses = () => api.get("/me/addresses").then((r) => r.data);
export const meAddAddress = (payload) => api.post("/me/addresses", payload).then((r) => r.data);
export const meDeleteAddress = (id) => api.delete(`/me/addresses/${id}`).then((r) => r.data);
export const meChangeSub = (code, action) =>
  api.post(`/me/subscriptions/${code}/${action}`).then((r) => r.data);

export const adminLogin = (key) => api.post("/admin/login", { key }).then((r) => r.data);
export const adminListOrders = (key) =>
  api.get("/admin/orders", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminStats = (key) =>
  api.get("/admin/stats", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminUpdateOrder = (key, code, patch) =>
  api.patch(`/admin/orders/${code}`, patch, { headers: { "X-Admin-Key": key } }).then((r) => r.data);

export const adminListSubscriptions = (key) =>
  api.get("/admin/subscriptions", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminListNotifications = (key) =>
  api.get("/admin/notifications", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminListReferrals = (key) =>
  api.get("/admin/referrals", { headers: { "X-Admin-Key": key } }).then((r) => r.data);

// Pricing — admin
export const adminListPricing = (key) =>
  api.get("/admin/pricing", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminUpdatePricing = (key, service_id, patch) =>
  api.patch(`/admin/pricing/${service_id}`, patch, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminCreatePricing = (key, payload) =>
  api.post("/admin/pricing", payload, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminDeletePricing = (key, service_id) =>
  api.delete(`/admin/pricing/${service_id}`, { headers: { "X-Admin-Key": key } }).then((r) => r.data);

// Coupons — public + admin
export const validateCoupon = (code, phone) =>
  api.get("/coupons/validate", { params: { code, phone } }).then((r) => r.data);
export const getActiveCoupons = () => api.get("/coupons/active").then((r) => r.data);
export const getUpcomingCoupons = (days = 3) =>
  api.get("/coupons/upcoming", { params: { days } }).then((r) => r.data);
export const adminListCoupons = (key) =>
  api.get("/admin/coupons", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminCreateCoupon = (key, payload) =>
  api.post("/admin/coupons", payload, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminUpdateCoupon = (key, code, patch) =>
  api.patch(`/admin/coupons/${code}`, patch, { headers: { "X-Admin-Key": key } }).then((r) => r.data);

// Posters — public
export const getPosters = (kind) =>
  api.get("/posters", { params: kind ? { kind } : {} }).then((r) => r.data);

// Posters — admin
export const adminListPosters = (key) =>
  api.get("/admin/posters", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminCreatePoster = (key, formData) =>
  api.post("/admin/posters", formData, {
    headers: { "X-Admin-Key": key, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
export const adminUpdatePoster = (key, id, patch) =>
  api.patch(`/admin/posters/${id}`, patch, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminDeletePoster = (key, id) =>
  api.delete(`/admin/posters/${id}`, { headers: { "X-Admin-Key": key } }).then((r) => r.data);

// Riders — public / self-service
export const riderRegister = (payload) => api.post("/rider/register", payload).then((r) => r.data);
export const riderLogin = (payload) => api.post("/rider/login", payload).then((r) => r.data);
export const riderMe = (token) =>
  api.get("/rider/me", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const riderJobs = (token) =>
  api.get("/rider/jobs", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const riderGrabJob = (token, code) =>
  api.post(`/rider/jobs/${code}/grab`, {}, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const riderUpdateJob = (token, code, patch) =>
  api.patch(`/rider/jobs/${code}`, patch, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const riderSummary = (token) =>
  api.get("/rider/summary", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);

// Riders — admin
export const adminListRiders = (key) =>
  api.get("/admin/riders", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminCreateRider = (key, payload) =>
  api.post("/admin/riders", payload, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminUpdateRider = (key, code, patch) =>
  api.patch(`/admin/riders/${code}`, patch, { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminAssignOrder = (key, code, rider_code) =>
  api.post(`/admin/orders/${code}/assign`, { rider_code }, { headers: { "X-Admin-Key": key } }).then((r) => r.data);

// OTP auth
export const requestOtp = (identifier, name) =>
  api.post("/auth/otp/request", { identifier, name }).then((r) => r.data);
export const verifyOtp = (identifier, code, name) =>
  api.post("/auth/otp/verify", { identifier, code, name }).then((r) => r.data);

// ---------- Admin: rankings (Search Console tracker) ----------
export const adminSectorStats = (key) =>
  api.get("/admin/sector-stats", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminListRankings = (key) =>
  api.get("/admin/rankings", { headers: { "X-Admin-Key": key } }).then((r) => r.data);
export const adminImportRankings = (key, csv, label) =>
  api
    .post("/admin/rankings/import", { csv, label }, { headers: { "X-Admin-Key": key } })
    .then((r) => r.data);
export const adminDeleteRanking = (key, id) =>
  api.delete(`/admin/rankings/${id}`, { headers: { "X-Admin-Key": key } }).then((r) => r.data);


export const STATUS_LABELS = {
  pending: "Pending pickup",
  picked_up: "Picked up",
  washing: "In wash",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const STATUS_COLORS = {
  pending: { bg: "#E28A71", ink: "#1A1A24" },
  picked_up: { bg: "#F0D48A", ink: "#1A1A24" },
  washing: { bg: "#92B4D2", ink: "#1A1A24" },
  out_for_delivery: { bg: "#D4E6B5", ink: "#1A1A24" },
  delivered: { bg: "#A3C4A3", ink: "#1A1A24" },
  cancelled: { bg: "#C4C4C4", ink: "#1A1A24" },
};
